import React, { useEffect, useState, cloneElement } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { HeartIcon, BookIcon, UsersIcon, FlameIcon, HeartHandshakeIcon, ListIcon, AlertCircleIcon, BookOpenIcon, MapPinIcon, UserIcon, ZapIcon, BookmarkIcon, SparklesIcon, CircleIcon, LinkIcon } from 'lucide-react';
import { FilterSidebar, FilterSidebarWrapper, NavItem, FilterSection } from '../components/UI/FilterSidebar';
import ContentPageHeader, { getContentPageIcon } from '../components/Content/ContentPageHeader';
import { ContentAccordionSection, sortEntriesAlphabetically } from '../components/UI/ContentAccordion';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { useDarkMode } from '../context/DarkModeContext';
type CategoryKey = 'all' | 'monogamous' | 'why-choose' | 'closed-triad' | 'structured-poly' | 'polyamorous' | 'nb-trans' | 'no-romance';
interface Category {
  key: CategoryKey;
  label: string;
  icon: JSX.Element;
  color: string;
}
interface PairingType {
  id: string;
  name: string;
  code: string;
  category: CategoryKey;
  count: number;
  popular: boolean;
  description?: string;
  connectionLogic?: string;
}
const PairingTypes: React.FC = () => {
  const [searchParams] = useSearchParams();
  const dk = useDarkClasses();
  const {
    darkMode
  } = useDarkMode();
  const [selectedCategory, setSelectedCategory] = useState<CategoryKey | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOption, setSortOption] = useState<'popularity' | 'alphabetical' | 'bookCount' | 'viewAll'>('alphabetical');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  useEffect(() => {
    const category = searchParams.get('category');
    setSelectedCategory(category as CategoryKey || 'all');
  }, [searchParams]);
  const categories: Category[] = [{
    key: 'all',
    label: 'All Pairing Types',
    icon: <HeartIcon className="h-4 w-4" data-id="element-12511" data-mp="thpf3qypuqoa" />,
    color: 'bg-pink-100 text-pink-800'
  }, {
    key: 'monogamous',
    label: 'Monogamous',
    icon: <HeartIcon className="h-4 w-4" data-id="element-12512" data-mp="yhv9vg0a8tal" />,
    color: 'bg-pink-100 text-pink-800'
  }, {
    key: 'why-choose',
    label: 'Why Choose',
    icon: <SparklesIcon className="h-4 w-4" data-id="element-12513" data-mp="17hrpks4p2hti" />,
    color: 'bg-rose-100 text-rose-800'
  }, {
    key: 'closed-triad',
    label: 'Closed Triad',
    icon: <UsersIcon className="h-4 w-4" data-id="element-12514" data-mp="1hewfn31nuxfl" />,
    color: 'bg-purple-100 text-purple-800'
  }, {
    key: 'structured-poly',
    label: 'Structured Poly',
    icon: <BookIcon className="h-4 w-4" data-id="element-12515" data-mp="1m6eg7pzruw01" />,
    color: 'bg-indigo-100 text-indigo-800'
  }, {
    key: 'polyamorous',
    label: 'Polyamorous',
    icon: <BookIcon className="h-4 w-4" data-id="element-12516" data-mp="1v72khszd5hzq" />,
    color: 'bg-violet-100 text-violet-800'
  }, {
    key: 'nb-trans',
    label: 'NB / Trans Inclusive',
    icon: <CircleIcon className="h-4 w-4" data-id="element-12517" data-mp="2031hu62o6aca" />,
    color: 'bg-teal-100 text-teal-800'
  }, {
    key: 'no-romance',
    label: 'No Romance',
    icon: <BookIcon className="h-4 w-4" data-id="element-12518" data-mp="259gbi5nffc0e" />,
    color: 'bg-gray-100 text-gray-800'
  }];
  const pairingTypes: PairingType[] = [
  // Monogamous
  {
    id: '1',
    name: 'MF',
    code: 'MF',
    category: 'monogamous',
    count: 0,
    popular: true,
    description: 'One male and one female protagonist in a central romance.',
    connectionLogic: 'Two partners, exclusively connected.'
  }, {
    id: '2',
    name: 'MM',
    code: 'MM',
    category: 'monogamous',
    count: 0,
    popular: true,
    description: 'Two male protagonists in a central romance.',
    connectionLogic: 'Two partners, exclusively connected.'
  }, {
    id: '3',
    name: 'FF',
    code: 'FF',
    category: 'monogamous',
    count: 0,
    popular: true,
    description: 'Two female protagonists in a central romance.',
    connectionLogic: 'Two partners, exclusively connected.'
  },
  // Why Choose
  {
    id: '4',
    name: 'FMM — Why Choose (2)',
    code: 'FMM',
    category: 'why-choose',
    count: 0,
    popular: true,
    description: 'One central FMC with two MMCs. The FMC is romantically connected to both; the MMCs are not romantically connected to each other.',
    connectionLogic: 'Hub-and-spoke: FMC is the hub, MMCs are spokes.'
  }, {
    id: '5',
    name: 'FMMM — Why Choose (3)',
    code: 'FMMM',
    category: 'why-choose',
    count: 0,
    popular: true,
    description: 'One central FMC with three MMCs. The FMC holds all romantic connections; the MMCs are not connected to each other.',
    connectionLogic: 'Hub-and-spoke: FMC is the hub, three MMC spokes.'
  }, {
    id: '6',
    name: 'FMMMM — Why Choose (4)',
    code: 'FMMMM',
    category: 'why-choose',
    count: 0,
    popular: false,
    description: 'One central FMC with four MMCs. A larger reverse harem configuration.',
    connectionLogic: 'Hub-and-spoke: FMC is the hub, four MMC spokes.'
  }, {
    id: '7',
    name: 'FMMMMM — Why Choose (5)',
    code: 'FMMMMM',
    category: 'why-choose',
    count: 0,
    popular: false,
    description: 'One central FMC with five MMCs. An extended reverse harem.',
    connectionLogic: 'Hub-and-spoke: FMC is the hub, five MMC spokes.'
  }, {
    id: '8',
    name: 'MFF — Why Choose (2)',
    code: 'MFF',
    category: 'why-choose',
    count: 0,
    popular: true,
    description: 'One central MMC with two FMCs. The MMC is romantically connected to both; the FMCs are not romantically connected to each other.',
    connectionLogic: 'Hub-and-spoke: MMC is the hub, FMCs are spokes.'
  }, {
    id: '9',
    name: 'MFFF — Why Choose (3)',
    code: 'MFFF',
    category: 'why-choose',
    count: 0,
    popular: false,
    description: 'One central MMC with three FMCs.',
    connectionLogic: 'Hub-and-spoke: MMC is the hub, three FMC spokes.'
  },
  // Closed Triad
  {
    id: '10',
    name: 'MMF — Closed Triad',
    code: 'MMF',
    category: 'closed-triad',
    count: 0,
    popular: true,
    description: 'Two males and one female, all three romantically connected to each other. A fully closed triad.',
    connectionLogic: 'All three partners interconnected.'
  }, {
    id: '11',
    name: 'FFM — Closed Triad',
    code: 'FFM',
    category: 'closed-triad',
    count: 0,
    popular: true,
    description: 'Two females and one male, all three romantically connected to each other.',
    connectionLogic: 'All three partners interconnected.'
  }, {
    id: '12',
    name: 'MMM — Closed Triad',
    code: 'MMM',
    category: 'closed-triad',
    count: 0,
    popular: false,
    description: 'Three males, all romantically connected to each other.',
    connectionLogic: 'All three partners interconnected.'
  }, {
    id: '13',
    name: 'FFF — Closed Triad',
    code: 'FFF',
    category: 'closed-triad',
    count: 0,
    popular: false,
    description: 'Three females, all romantically connected to each other.',
    connectionLogic: 'All three partners interconnected.'
  },
  // Structured Poly
  {
    id: '14',
    name: 'MMFMM — Structured Poly',
    code: 'MMFMM',
    category: 'structured-poly',
    count: 0,
    popular: true,
    description: 'Two connected MMCs, one central FMC, two more connected MMCs. The first pair and second pair are bonded within their subgroups; the FMC connects across both.',
    connectionLogic: 'Subgroup bonded: [MM]F[MM] — order indicates sub-pair connections.'
  }, {
    id: '15',
    name: 'FFMFF — Structured Poly',
    code: 'FFMFF',
    category: 'structured-poly',
    count: 0,
    popular: false,
    description: 'Two connected FMCs, one central MMC, two more connected FMCs.',
    connectionLogic: 'Subgroup bonded: [FF]M[FF] — order indicates sub-pair connections.'
  }, {
    id: '16',
    name: 'MMFF — Structured Poly',
    code: 'MMFF',
    category: 'structured-poly',
    count: 0,
    popular: false,
    description: 'Two connected MMCs and two connected FMCs, with cross-connections between the pairs.',
    connectionLogic: 'Two bonded pairs with cross-connections.'
  }, {
    id: '17',
    name: 'MFMF — Structured Poly',
    code: 'MFMF',
    category: 'structured-poly',
    count: 0,
    popular: false,
    description: 'Alternating male/female partners in a structured configuration where order indicates bond groupings.',
    connectionLogic: 'Order-based bonding: adjacent partners connected.'
  },
  // Polyamorous
  {
    id: '18',
    name: 'Open Polycule',
    code: 'Polycule',
    category: 'polyamorous',
    count: 0,
    popular: true,
    description: 'Multiple partners, all romantically interconnected. No single central character — all bonds are mutual and fully meshed.',
    connectionLogic: 'Fully interconnected: all partners connected to all others.'
  }, {
    id: '19',
    name: 'V Configuration',
    code: 'V-Poly',
    category: 'polyamorous',
    count: 0,
    popular: false,
    description: 'One partner (the "hinge") is romantically connected to two others who are not connected to each other. Distinct from Why Choose in that the hinge role is shared or fluid.',
    connectionLogic: 'Hinge-and-arms: one partner bridges two others.'
  }, {
    id: '20',
    name: 'Quad',
    code: 'Quad',
    category: 'polyamorous',
    count: 0,
    popular: false,
    description: 'Four partners, all romantically connected to each other.',
    connectionLogic: 'Fully interconnected: four partners.'
  },
  // NB / Trans Inclusive
  {
    id: '21',
    name: 'NB',
    code: 'NB',
    category: 'nb-trans',
    count: 0,
    popular: true,
    description: 'Non-binary protagonist. Pairing structure unspecified or not central to the story.',
    connectionLogic: 'Gender identity: non-binary lead.'
  }, {
    id: '22',
    name: 'NB + M',
    code: 'NB+M',
    category: 'nb-trans',
    count: 0,
    popular: true,
    description: 'Non-binary protagonist paired with a male love interest.',
    connectionLogic: 'Two partners: NB and male.'
  }, {
    id: '23',
    name: 'NB + F',
    code: 'NB+F',
    category: 'nb-trans',
    count: 0,
    popular: true,
    description: 'Non-binary protagonist paired with a female love interest.',
    connectionLogic: 'Two partners: NB and female.'
  }, {
    id: '24',
    name: 'NB + NB',
    code: 'NB+NB',
    category: 'nb-trans',
    count: 0,
    popular: false,
    description: 'Two non-binary protagonists in a central romance.',
    connectionLogic: 'Two partners, both non-binary.'
  }, {
    id: '25',
    name: 'FtM',
    code: 'FtM',
    category: 'nb-trans',
    count: 0,
    popular: true,
    description: 'Trans male protagonist. Pairing structure unspecified or not central.',
    connectionLogic: 'Gender identity: trans male lead.'
  }, {
    id: '26',
    name: 'MtF',
    code: 'MtF',
    category: 'nb-trans',
    count: 0,
    popular: true,
    description: 'Trans female protagonist. Pairing structure unspecified or not central.',
    connectionLogic: 'Gender identity: trans female lead.'
  }, {
    id: '27',
    name: 'FtM + F',
    code: 'FtM+F',
    category: 'nb-trans',
    count: 0,
    popular: false,
    description: 'Trans male protagonist paired with a female love interest.',
    connectionLogic: 'Two partners: trans male and female.'
  }, {
    id: '28',
    name: 'FtM + M',
    code: 'FtM+M',
    category: 'nb-trans',
    count: 0,
    popular: false,
    description: 'Trans male protagonist paired with a male love interest.',
    connectionLogic: 'Two partners: trans male and male.'
  }, {
    id: '29',
    name: 'MtF + M',
    code: 'MtF+M',
    category: 'nb-trans',
    count: 0,
    popular: false,
    description: 'Trans female protagonist paired with a male love interest.',
    connectionLogic: 'Two partners: trans female and male.'
  }, {
    id: '30',
    name: 'MtF + F',
    code: 'MtF+F',
    category: 'nb-trans',
    count: 0,
    popular: false,
    description: 'Trans female protagonist paired with a female love interest.',
    connectionLogic: 'Two partners: trans female and female.'
  }, {
    id: '31',
    name: 'NB Why Choose',
    code: 'NB+MM',
    category: 'nb-trans',
    count: 0,
    popular: false,
    description: 'Non-binary protagonist with multiple love interests in a Why Choose configuration.',
    connectionLogic: 'Hub-and-spoke: NB is the hub.'
  },
  // No Romance
  {
    id: '32',
    name: 'No Central Romance',
    code: 'None',
    category: 'no-romance',
    count: 0,
    popular: true,
    description: 'No romantic relationship is central to the story. Focus is on plot, character growth, mystery, or other themes.',
    connectionLogic: 'No romantic pairing.'
  }, {
    id: '33',
    name: 'Platonic Central Relationship',
    code: 'Platonic',
    category: 'no-romance',
    count: 0,
    popular: false,
    description: 'A deep, central non-romantic relationship drives the story — found family, best friends, or a mentor bond.',
    connectionLogic: 'Non-romantic central bond.'
  }];
  const filteredTypes = pairingTypes.filter(pt => {
    if (selectedCategory !== 'all' && pt.category !== selectedCategory) return false;
    if (searchQuery && !pt.name.toLowerCase().includes(searchQuery.toLowerCase()) && !pt.code.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });
  const sortedTypes = [...filteredTypes].sort((a, b) => {
    if (sortOption === 'alphabetical') return a.name.localeCompare(b.name);
    if (sortOption === 'bookCount') return b.count - a.count;
    if (sortOption === 'viewAll') return a.name.localeCompare(b.name);
    if (a.popular !== b.popular) return a.popular ? -1 : 1;
    return b.count - a.count;
  });
  const grouped: Record<CategoryKey, PairingType[]> = {
    monogamous: [],
    'why-choose': [],
    'closed-triad': [],
    'structured-poly': [],
    polyamorous: [],
    'nb-trans': [],
    'no-romance': [],
    all: []
  };
  sortedTypes.forEach(pt => {
    grouped[pt.category].push(pt);
  });
  const getColor = (category: CategoryKey): string => {
    if (darkMode) {
      switch (category) {
        case 'monogamous':
          return 'bg-pink-900/30 text-pink-300';
        case 'why-choose':
          return 'bg-rose-900/30 text-rose-300';
        case 'closed-triad':
          return 'bg-purple-900/30 text-purple-300';
        case 'structured-poly':
          return 'bg-indigo-900/30 text-indigo-300';
        case 'polyamorous':
          return 'bg-violet-900/30 text-violet-300';
        case 'nb-trans':
          return 'bg-teal-900/30 text-teal-300';
        case 'no-romance':
          return 'bg-gray-700 text-gray-300';
        default:
          return dk.badgeNeutral;
      }
    }
    return categories.find(c => c.key === category)?.color || 'bg-gray-100 text-gray-800';
  };
  const categorySubtitles: Record<CategoryKey, string> = {
    all: '',
    monogamous: 'Two partners',
    'why-choose': 'Hub & Spoke',
    'closed-triad': 'All interconnected',
    'structured-poly': 'Subgroup bonded',
    polyamorous: 'Fully meshed',
    'nb-trans': 'Inclusive configurations',
    'no-romance': 'No romantic pairing'
  };
  const categoryTooltips: Record<CategoryKey, string> = {
    all: '',
    monogamous: 'Two partners exclusively connected to each other. No additional romantic bonds.',
    'why-choose': 'One central character connected to multiple partners. The other partners are not romantically connected to each other — hub-and-spoke structure.',
    'closed-triad': 'Three partners all romantically connected to each other. Every bond is mutual and fully closed.',
    'structured-poly': 'Multi-partner dynamic where order indicates sub-group bonds. Adjacent or grouped partners share connections, but not necessarily cross-group.',
    polyamorous: 'Multiple partners, all fully interconnected. Every partner shares romantic bonds with all others in the group.',
    'nb-trans': 'Pairings featuring non-binary, trans male, or trans female protagonists. Covers a range of romantic structures.',
    'no-romance': 'No central romantic relationship. Story focus is on plot, character growth, or platonic bonds.'
  };
  const categoryPrefix: Record<CategoryKey, string> = {
    all: '',
    monogamous: '',
    'why-choose': 'W/C',
    'closed-triad': 'Triad',
    'structured-poly': 'S/P',
    polyamorous: 'Poly',
    'nb-trans': '',
    'no-romance': ''
  };
  const filterSections: FilterSection[] = [{
    id: 'category',
    title: 'Category',
    type: 'radio',
    options: [{
      value: 'all',
      label: 'All Pairing Types'
    }, {
      value: 'monogamous',
      label: 'Monogamous'
    }, {
      value: 'why-choose',
      label: 'Why Choose'
    }, {
      value: 'closed-triad',
      label: 'Closed Triad'
    }, {
      value: 'structured-poly',
      label: 'Structured Poly'
    }, {
      value: 'polyamorous',
      label: 'Polyamorous'
    }, {
      value: 'nb-trans',
      label: 'NB / Trans Inclusive'
    }, {
      value: 'no-romance',
      label: 'No Romance'
    }],
    value: selectedCategory,
    onChange: (val: string) => setSelectedCategory(val as CategoryKey)
  }, {
    id: 'sort',
    title: 'Sort By',
    type: 'radio',
    options: [{
      value: 'popularity',
      label: 'Popularity'
    }, {
      value: 'alphabetical',
      label: 'Alphabetical'
    }, {
      value: 'bookCount',
      label: 'Book Count'
    }, {
      value: 'viewAll',
      label: 'View All (flat list)'
    }],
    value: sortOption,
    onChange: (val: string) => setSortOption(val as typeof sortOption)
  }];
  const handleReset = () => {
    setSelectedCategory('all');
    setSortOption('popularity');
    setSearchQuery('');
  };
  const contentNavItems: NavItem[] = [{
    label: 'View All Tags',
    href: '/tagged-books?type=all',
    icon: ListIcon
  }, {
    label: 'Tropes',
    href: '/tropes',
    icon: HeartHandshakeIcon,
    subItems: [{
      label: 'Romance',
      href: '/tropes?category=romance'
    }, {
      label: 'Fantasy',
      href: '/tropes?category=fantasy'
    }, {
      label: 'Mystery',
      href: '/tropes?category=mystery'
    }, {
      label: 'Sci-Fi',
      href: '/tropes?category=scifi'
    }, {
      label: 'Horror',
      href: '/tropes?category=horror'
    }, {
      label: 'General',
      href: '/tropes?category=general'
    }]
  }, {
    label: 'Characters',
    href: '/characters',
    icon: UserIcon,
    subItems: [{
      label: 'Romance',
      href: '/characters?category=romance'
    }, {
      label: 'Fantasy',
      href: '/characters?category=fantasy'
    }, {
      label: 'Mystery',
      href: '/characters?category=mystery'
    }, {
      label: 'Sci-Fi',
      href: '/characters?category=scifi'
    }, {
      label: 'Horror',
      href: '/characters?category=horror'
    }, {
      label: 'General',
      href: '/characters?category=general'
    }]
  }, {
    label: 'Relationship Types',
    href: '/relationship-types',
    icon: UsersIcon,
    subItems: [{
      label: 'Romance',
      href: '/relationship-types?category=romance'
    }, {
      label: 'Fantasy',
      href: '/relationship-types?category=fantasy'
    }, {
      label: 'Mystery',
      href: '/relationship-types?category=mystery'
    }, {
      label: 'Sci-Fi',
      href: '/relationship-types?category=scifi'
    }, {
      label: 'Horror',
      href: '/relationship-types?category=horror'
    }, {
      label: 'General',
      href: '/relationship-types?category=general'
    }]
  }, {
    label: 'Pairing Types',
    href: '/pairing-types',
    icon: LinkIcon,
    isActive: true,
    subItems: [{
      label: 'Monogamous',
      href: '/pairing-types?category=monogamous'
    }, {
      label: 'Why Choose',
      href: '/pairing-types?category=why-choose'
    }, {
      label: 'Closed Triad',
      href: '/pairing-types?category=closed-triad'
    }, {
      label: 'Structured Poly',
      href: '/pairing-types?category=structured-poly'
    }, {
      label: 'Polyamorous',
      href: '/pairing-types?category=polyamorous'
    }, {
      label: 'NB / Trans Inclusive',
      href: '/pairing-types?category=nb-trans'
    }, {
      label: 'No Romance',
      href: '/pairing-types?category=no-romance'
    }]
  }, {
    label: 'Spice & Darkness',
    href: '/spice-darkness',
    icon: FlameIcon,
    subItems: [{
      label: 'Spice Levels',
      href: '/spice-darkness#spice'
    }, {
      label: 'Darkness Levels',
      href: '/spice-darkness#darkness'
    }]
  }, {
    label: 'Burn Levels',
    href: '/burn-levels',
    icon: ZapIcon
  }, {
    label: 'Ending Types',
    href: '/ending-types',
    icon: BookmarkIcon
  }, {
    label: 'Representation',
    href: '/representation',
    icon: UsersIcon,
    subItems: [{
      label: 'Sexual Orientation & Gender',
      href: '/representation?category=Sexual+Orientation+%26+Gender'
    }, {
      label: 'Race & Ethnicity',
      href: '/representation?category=Race+%26+Ethnicity'
    }, {
      label: 'Neurodiversity & Disability',
      href: '/representation?category=Neurodiversity+%26+Disability'
    }, {
      label: 'Mental Health',
      href: '/representation?category=Mental+Health'
    }]
  }, {
    label: 'Themes',
    href: '/themes',
    icon: BookOpenIcon,
    subItems: [{
      label: 'Personal Growth',
      href: '/themes?category=Personal+Growth'
    }, {
      label: 'Destiny',
      href: '/themes?category=Destiny'
    }, {
      label: 'Power',
      href: '/themes?category=Power'
    }, {
      label: 'Morality',
      href: '/themes?category=Morality'
    }, {
      label: 'Relationships',
      href: '/themes?category=Relationships'
    }, {
      label: 'Identity',
      href: '/themes?category=Identity'
    }]
  }, {
    label: 'Settings',
    href: '/settings',
    icon: MapPinIcon,
    subItems: [{
      label: 'Contemporary',
      href: '/settings?category=Contemporary'
    }, {
      label: 'Historical',
      href: '/settings?category=Historical'
    }, {
      label: 'Fantasy',
      href: '/settings?category=Fantasy'
    }, {
      label: 'Science Fiction',
      href: '/settings?category=Science%20Fiction'
    }]
  }, {
    label: 'Content Warnings',
    href: '/content-warnings',
    icon: AlertCircleIcon
  }];
  const renderCard = (pt: PairingType, prefix: string) => {
    const displayName = prefix ? `${prefix} — ${pt.code}` : pt.name;
    return <Link key={pt.id} to={`/tagged-books/${pt.code.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9+\-]/g, '')}`} className={`${getColor(pt.category as CategoryKey)} rounded-lg p-3.5 hover:shadow-md transition-shadow flex flex-col gap-1`} data-id="element-12519" data-mp="1phx053pfptcy">
        <div className="flex justify-between items-center gap-2" data-id="element-12520" data-mp="1hwl490ezijnx">
          <div className="font-semibold text-sm leading-snug" data-id="element-12521" data-mp="1cy9oya5x7gyb">
            {displayName}
          </div>
          <div className="text-xs opacity-70 shrink-0 tabular-nums" data-id="element-12522" data-mp="17zygo3v9xei7">
            {pt.count} books
          </div>
        </div>
        {pt.description && <p className="text-xs opacity-80 leading-relaxed" data-id="element-12523" data-mp="z7mskonmu35e">{pt.description}</p>}
      </Link>;
  };
  const renderTypes = () => {
    if (sortOption === 'viewAll' || selectedCategory !== 'all' || searchQuery) {
      return <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-12524" data-mp="pbjqfiss64ig">
          {sortedTypes.map(pt => renderCard(pt, categoryPrefix[pt.category]))}
        </div>;
    }
    const categoryOrder = ['monogamous', 'why-choose', 'closed-triad', 'structured-poly', 'polyamorous', 'nb-trans', 'no-romance'];
    const orderedEntries = categoryOrder.map(key => [key, grouped[key as CategoryKey]] as [string, PairingType[]]).filter(([, types]) => types.length > 0);
    return <div className="space-y-3" data-id="element-12525" data-mp="1mbr75q1huu1">
        {orderedEntries.map(([category, types]) => {
        const categoryObj = categories.find(c => c.key === category);
        const prefix = categoryPrefix[category as CategoryKey];
        return <ContentAccordionSection key={category} title={categoryObj?.label || category} icon={cloneElement(categoryObj?.icon || <HeartIcon className="h-5 w-5" data-id="element-12527" data-mp="kkfpf54e29h2" />, {
          className: 'h-5 w-5'
        })} itemCount={types.length} subtitle={categorySubtitles[category as CategoryKey]} subtitleTooltip={categoryTooltips[category as CategoryKey]} data-id="element-12526" data-mp="td9koc08djgk">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-12528" data-mp="1mehd6vyqyhfg">
                {types.map(pt => renderCard(pt, prefix))}
              </div>
            </ContentAccordionSection>;
      })}
      </div>;
  };
  return <div className={dk.page} data-id="element-12529" data-mp="1r7k6d2zm3ov4">
      <ContentPageHeader title="Pairing Types" icon={getContentPageIcon('pairingtypes')} searchPlaceholder="Search pairing types or codes..." searchQuery={searchQuery} setSearchQuery={setSearchQuery} showFilters={sidebarOpen} setShowFilters={setSidebarOpen} data-id="element-12530" data-mp="1r8llbijr0vi4" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-12531" data-mp="1dh73vg5hp2d6">
        <div className="flex gap-8" data-id="element-12532" data-mp="18frexd06ybpi">
          <FilterSidebarWrapper isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} data-id="element-12533" data-mp="18ivs2zvmwhsb">
            <FilterSidebar title="Browse" navigation={contentNavItems} sections={filterSections} onReset={handleReset} searchable={false} data-id="element-12534" data-mp="i7lb9fwafki8" />
          </FilterSidebarWrapper>
          <main className="flex-1 min-w-0" data-id="element-12535" data-mp="r5dswqphf48s">
            {sortedTypes.length === 0 ? <div className="text-center py-12" data-id="element-12536" data-mp="vzi11dahhibg">
                <p className={dk.textMuted} data-id="element-12537" data-mp="10xtd7uo944wu">
                  No pairing types found matching your criteria.
                </p>
              </div> : renderTypes()}
          </main>
        </div>
      </div>
    </div>;
};
export default PairingTypes;